import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Row, Col, Typography, Button, Grid, Layout, Space } from 'antd';
import './Blog.css';
import '../Category/Category.css'; // Import sidebar styles
import pic1 from '../../assets/images/pic-1.webp';

import { useSearch } from '../../contexts/SearchContext';
import { ArticleService, CategoryService, WPPost, WPCategory } from '../../services/api.services';
import { categoryRecipes } from '../../data/recipes';

const { Title, Paragraph, Text } = Typography;
const { useBreakpoint } = Grid;
const { Content } = Layout;

const categoryBannerImage = pic1;

// Accordion removed as requested

const BlogPage: React.FC = () => {
  const screens = useBreakpoint();
  const { searchQuery, setSearchQuery } = useSearch();
  const isMobile = !screens.md;
  const navigate = useNavigate();

  const [searchParams, setSearchParams] = useSearchParams();
  const categoryParam = searchParams.get('category');

  const [posts, setPosts] = useState<WPPost[]>([]);
  const [categories, setCategories] = useState<WPCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const [localSearchTerm, setLocalSearchTerm] = useState(searchQuery);

  // Sync global search with local input
  useEffect(() => {
    setLocalSearchTerm(searchQuery);
  }, [searchQuery]);

  // Reset page and posts when filters change
  useEffect(() => {
    setPage(1);
    setPosts([]);
    setHasMore(true);
  }, [categoryParam, searchQuery]);

  // Fetch Categories once on mount
  useEffect(() => {
    CategoryService.getAll().then(res => {
      setCategories(res.data);
    }).catch(console.error);
  }, []);

  // Fetch Recipes when page or filters change
  useEffect(() => {
    if (page === 1) setLoading(true);
    else setLoadingMore(true);

    ArticleService.getPaginatedRecipes({ 
      page, 
      search: searchQuery, 
      category: categoryParam || undefined 
    }).then(res => {
      if (page === 1) {
        setPosts(res.data);
      } else {
        setPosts(prev => [...prev, ...res.data]);
      }
      setHasMore(res.page < res.totalPages);
    }).catch(e => {
      console.error(e);
      if (page === 1 && posts.length === 0) {
        // Fallback for demo if backend not ready
        setPosts(categoryRecipes as any[]);
        setHasMore(false);
      }
    }).finally(() => {
      setLoading(false);
      setLoadingMore(false);
    });
  }, [page, categoryParam, searchQuery]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchQuery(localSearchTerm);
  };

  const filteredPosts = posts; // We keep the variable name to minimize JSX changes




  const renderPostCard = (post: WPPost, index: number) => (
    <Col xs={24} sm={12} lg={8} key={post.id ?? index}>
     <Layout style={{ background: 'transparent', height: '100%', display: 'flex', flexDirection: 'column' }}>
        <div style={{ width: '100%', aspectRatio: '4/5', overflow: 'hidden', marginBottom: 20 }}>
          <Link to={`/blog-details/${post.slug || post.id}`} style={{ display: 'block', width: '100%', height: '100%' }}>
            <img
              src={post.image || ''}
              alt={post.title}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: 'block',
                transition: 'transform 0.3s ease'
              }}
              onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
              onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
            />
          </Link>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
          <Link to={`/blog-details/${post.slug || post.id}`} style={{ textDecoration: 'none', color: 'inherit' }}>
            <Title 
              level={4} 
              style={{ 
                margin: 0, 
                fontFamily: 'Playfair Display, serif', 
                lineHeight: 1.3,
                marginBottom: 12,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                height: '2.6em'
              }}
            >
              {post.title}
            </Title>
          </Link>

          <Space size={12} style={{ marginBottom: 12 }}>
            {post.category && post.category.toUpperCase() !== 'UNCATEGORIZED' && (
              <Text strong style={{ fontSize: 10, letterSpacing: 1.5, color: '#306046', textTransform: 'uppercase' }}>
                {post.category}
              </Text>
            )}
            <Text type="secondary" style={{ fontSize: 11 }}>
              {post.date}
            </Text>
          </Space>

          <Paragraph 
            style={{ 
              color: '#666', 
              fontSize: 14,
              lineHeight: 1.5,
              margin: 0,
              marginBottom: 20,
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              height: '4.5em'
            }}
          >
            {post.excerpt}
          </Paragraph>

          <div style={{ marginTop: 'auto' }}>
            <Link to={`/blog-details/${post.slug || post.id}`}>
              <Button
                size={isMobile ? "large" : "middle"}
                className="read-more-btn"
                style={{
                  background: '#306046',
                  color: '#fff',
                  borderRadius: 30,
                  border: 'none',
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: 1,
                  textTransform: 'uppercase'
                }}
              >
                READ MORE
              </Button>
            </Link>
          </div>
        </div>
      </Layout>
    </Col>
  );

  return (
    <Content className="blog-container-antd" style={{ background: '#fff' }}>
      {/* 1. HERO BANNER */}
      <section
        className="blog-hero"
        style={{ backgroundImage: `url("${categoryBannerImage}")` }}
      >
        <div className="blog-hero-overlay" />
        <div className="blog-hero-content">
          <p className="blog-hero-label">RECIPE INDEX</p>
          <h1 className="blog-hero-heading">
            {searchQuery ? (
              <>Search Results For:<br/>"{searchQuery}"</>
            ) : (
              <>Browse my collection of<br/>healthy and delicious recipes.</>
            )}
          </h1>
          <p className="blog-hero-signature">Loganayaki Thamilselvan</p>
        </div>
      </section>

      {/* 2. MAIN LAYOUT (2 Columns) */}
      <div className="recipe-page-container">
        {/* Left Column: Recipes */}
        <div className="recipes-column">
          {loading ? (
            <div style={{ textAlign: 'center', padding: '60px 0' }}>
              <div style={{ display: 'inline-block', width: 44, height: 44, border: '3px solid #eee', borderTop: '3px solid #306046', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>
          ) : filteredPosts.length > 0 ? (
            <>
              <Row gutter={[isMobile ? 0 : 32, 48]}>
                {filteredPosts.map((post, index) => renderPostCard(post, index))}
              </Row>
              
              {hasMore && (
                <div style={{ display: 'flex', justifyContent: 'center', marginTop: isMobile ? 40 : 60 }}>
                  <Button
                    size="middle"
                    className="load-more-posts-btn"
                    onClick={() => setPage(prev => prev + 1)}
                    loading={loadingMore}
                    style={{
                      background: '#306046',
                      color: '#fff',
                      padding: '0 25px',
                      height: 38,
                      borderRadius: 20,
                      fontSize: 12,
                      fontWeight: 700,
                      letterSpacing: 1.5,
                      border: 'none',
                      boxShadow: '0 4px 15px rgba(48, 96, 70, 0.2)'
                    }}
                  >
                    LOAD MORE POSTS
                  </Button>
                </div>
              )}
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: '100px 0' }}>
              <Title level={3} style={{ color: '#888' }}>No recipes found.</Title>
              <Button type="text" onClick={() => {setSearchQuery(''); navigate('/recipe');}} style={{ color: '#306046', fontWeight: 'bold' }}>
                Clear Filters
              </Button>
            </div>
          )}
        </div>

        {/* Right Column: Sidebar */}
        <aside className="category-sidebar">
          <div className="sidebar-title">Search & Filters</div>
          
          <form className="sidebar-search-wrapper" onSubmit={handleSearch}>
            <input
              type="text"
              className="sidebar-search-input"
              placeholder="Search for a recipe"
              value={localSearchTerm}
              onChange={(e) => setLocalSearchTerm(e.target.value)}
            />
            <svg
              className="sidebar-search-icon"
              viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
            >
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
          </form>

          <div className="accordion-wrapper">
            {/* "All Recipes" Link in Sidebar */}
            <div 
              className="sidebar-category-link" 
              style={{ 
                cursor: 'pointer', 
                padding: '12px 0', 
                borderBottom: '1px solid #eee',
                color: !categoryParam ? '#306046' : '#444',
                fontWeight: !categoryParam ? 'bold' : 'normal',
                transition: 'color 0.2s'
              }} 
              onClick={() => {
                navigate('/recipe');
              }}
            >
              All Recipes
            </div>

            {categories.filter(cat => {
              const name = cat.name.toLowerCase();
              const slug = cat.slug.toLowerCase();
              return name !== 'uncategorized' && name !== 'author page' && name !== 'author pages' && slug !== 'loganayaki-pages';
            }).map(cat => {
              const isActive = categoryParam && (categoryParam.toLowerCase() === cat.slug.toLowerCase() || categoryParam === String(cat.id));
              return (
                <div 
                  key={cat.id}
                  className="sidebar-category-link" 
                  style={{ 
                    cursor: 'pointer', 
                    padding: '12px 0', 
                    borderBottom: '1px solid #eee',
                    color: isActive ? '#306046' : '#444',
                    fontWeight: isActive ? 'bold' : 'normal',
                    display: 'flex',
                    justifyContent: 'space-between',
                    transition: 'color 0.2s'
                  }} 
                  onClick={() => {
                    navigate(`/recipe?category=${cat.slug}`);
                  }}
                >
                  <span>{cat.name}</span>
                  {cat.count !== undefined && <span style={{ fontSize: '11px', color: '#888', fontWeight: 'normal' }}>({cat.count})</span>}
                </div>
              );
            })}
            
          </div>
        </aside>
      </div>
    </Content>
  );
};

export default BlogPage;
